import { IUserSkill } from '@common/microservice-types/db/skill/skill.user';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

export class ChatSkillSelectionStore
{
    private readonly currentSkill$ = new BehaviorSubject<IUserSkill | null>(null);
    private readonly accountSubscription: Subscription;
    private storageKey: string | null = null;
    private selectedSkillId: number | null = null;
    private availableSkills: IUserSkill[] = [];

    constructor(userId$: Observable<string | null>)
    {
        this.accountSubscription = userId$.subscribe(userId =>
        {
            const storageKey = userId ? `r2.addin.skills.activeSkillId:${ userId }` : null;
            if (storageKey === this.storageKey) { return; }

            this.storageKey = storageKey;
            this.selectedSkillId = this.readSelectedSkillId();
            this.restoreSelectedSkill();
        });
    }

    public setCurrentSkill(skill: IUserSkill | null): void
    {
        this.selectedSkillId = skill?.id ?? null;
        this.currentSkill$.next(skill);
        if (!this.storageKey) { return; }

        try
        {
            if (this.selectedSkillId === null)
            {
                localStorage.removeItem(this.storageKey);
            }
            else
            {
                localStorage.setItem(this.storageKey, String(this.selectedSkillId));
            }
        }
        catch
        {
            // Keep skill selection usable when storage is unavailable.
        }
    }

    public getCurrentSkill(): IUserSkill | null
    {
        return this.currentSkill$.getValue();
    }

    public $getCurrentSkill(): Observable<IUserSkill | null>
    {
        return this.currentSkill$.asObservable();
    }

    public setSkills(skills: IUserSkill[]): void
    {
        this.availableSkills = skills;
        this.restoreSelectedSkill();
    }

    public onDestroy(): void
    {
        this.accountSubscription.unsubscribe();
    }

    private readSelectedSkillId(): number | null
    {
        if (!this.storageKey) { return null; }
        try
        {
            const id = Number(localStorage.getItem(this.storageKey));
            return Number.isSafeInteger(id) && id > 0 ? id : null;
        }
        catch
        {
            return null;
        }
    }

    private restoreSelectedSkill(): void
    {
        this.currentSkill$.next(this.availableSkills.find(skill => skill.id === this.selectedSkillId) ?? null);
    }
}
